import _ from 'lodash';
import { NextSeo } from 'next-seo';
import { useWindowSize } from 'react-use';
import Script from 'next/script';
import { useRouter } from 'next/router';
import React from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Modal from 'react-modal';
import useSWR from 'swr';
import Confetti from 'react-confetti';
import lottie from 'lottie-web';

import api, { swrFetcher } from 'utils/api';
import { useGlobalConfig } from 'contexts/GlobalConfig';
import { useUserAuth } from 'contexts/UserAuth';
import { useFirebaseAuth } from 'contexts/FirebaseAuth';
import LandingModal from 'components/2025/LandingModal';
import BeautyAward2025Modal from 'components/2025/BeautyAward2025Modal';
import WebpImg from 'components/WebpImg';

gsap.registerPlugin(useGSAP);

const { useState, useEffect, useRef } = React;

const BeautyAwards2025LuckyReels = () => {
  const router = useRouter();
  const { width, height } = useWindowSize();

  const { user, getAuthorizationHeader, isSignInStatusReady } = useUserAuth();
  const { isSignIn } = useFirebaseAuth();

  const containerRef = useRef(null);
  // const wheelRef = useRef(null);
  // const buttonRef = useRef(null);

  const endAngle = useRef(null);
  const stopRef = useRef(false);
  const audioRef = useRef(null);

  const tapRef = useRef(null);
  const lottieRef = useRef(null);
  const lottieAnim = useRef(null);

  const [selectedAward, setSelectedAward] = useState(null);
  const [luckyDraw, setLuckyDraw] = useState(null);
  const [animateCompleted, setAnimateCompleted] = useState(false);

  const [showLandingModal, setShowLandingModal] = useState(false);

  const submitAnswers = async (answers) => {
    const headers = await getAuthorizationHeader();
    try {
      await api.post(
        `/api/beauty-award/votingAnswer`,
        {
          details: answers,
        },
        { headers }
      );
      localStorage.removeItem('beauty_award_2025_votings');
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    const existed = localStorage.getItem('beauty_award_2025_votings');
    if (
      !_.isEmpty(existed) &&
      isSignInStatusReady &&
      user &&
      showLandingModal == false
    ) {
      const { phone_verified } = user;
      if (!phone_verified) {
        window.location = '/phone';
        return;
      }
      const parsedVotingResult = JSON.parse(existed);
      if (parsedVotingResult) {
        submitAnswers(parsedVotingResult);
      }
      setShowLandingModal(true);
    }
  }, [user, isSignInStatusReady, showLandingModal]);

  useEffect(() => {
    const audio = new Audio('/lotties/egg/rotating.mp3');
    audioRef.current = audio;

    const onComplete = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setAnimateCompleted(true);
    };

    if (lottieRef.current) {
      lottieAnim.current = lottie.loadAnimation({
        name: 'egg',
        container: lottieRef.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: '/lotties/egg/animation.json',
      });
      lottieAnim.current.addEventListener('complete', onComplete);
    }
    return () => {
      if (lottieAnim.current) {
        lottieAnim.current.removeEventListener('complete', onComplete);
        lottieAnim.current.destroy();
        lottieAnim.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedAward && animateCompleted) {
      setShowAwardModal(true);
    }
  }, [selectedAward, animateCompleted]);

  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showAwardFormModal, setShowAwardFormModal] = useState(false);

  const { data: awards } = useSWR('/api/beauty-award/awards', swrFetcher, {
    fallbackData: [],
  });

  const { data: myLuckyDraw } = useSWR(
    user ? '/api/beauty-award/me/luckyDraw' : null,
    (url) => swrFetcher(url, getAuthorizationHeader),
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (isSignIn === false) {
      window.location.href = '/beautyawards2025/login';
    }
  }, [isSignIn]);

  useEffect(() => {
    if (_.isEmpty(luckyDraw) && !_.isEmpty(myLuckyDraw)) {
      window.location.href = '/profile/redeem';
    }
  }, [myLuckyDraw, luckyDraw]);

  return (
    <>
      <NextSeo
        title="Sa Sa 美麗「程」獻：2025 Sorra Beauty Awards |  最真實的美妝產品評價 | Sorra"
        description="Sa Sa x Sorra年度票選大賞正式開始！第2屆的主題為「Fly me to the ...」，希望大家在選出陪你渡過2025年旅程的年度好物，打造香港最貼地的美妝排行榜。"
        openGraph={{
          images: [
            {
              url: 'https://www.sorra.net/images/beauty-award/2025/banner_launching.png',
              width: 1200,
              height: 410,
              alt: 'Sorra',
            },
          ],
        }}
      />
      <div
        ref={containerRef}
        style={{
          background:
            'linear-gradient(206deg, var(--Light-pink, #FFE5E9) 35.86%, rgba(235, 89, 148, 0.50) 73.84%)',
        }}
        className="no-scrollbar relative min-h-dvh overflow-x-hidden text-white"
      >
        <div className="flex items-center justify-center gap-x-6 py-7">
          <div className="flex items-end gap-x-1.5">
            <img
              src="/images/beauty-award/2025/sasa.png"
              className="h-[25px] w-[69px] object-contain"
            />
            <p className="text-[10px] font-normal text-[#414042]">
              美麗「程」獻
            </p>
          </div>
          <img
            src="/images/beauty-award/2025/sorra_2025_luckyreels.png"
            className="h-[58px] w-[99px] object-contain"
          />
        </div>
        <div className="flex justify-center">
          <div className="flex flex-col rounded border border-[#4F4E4F] bg-[#FCECDF] px-6 py-2">
            <p className="text-center text-base font-medium text-[#4F4E4F]">
              感謝參與 Sa Sa美麗「程」獻 ：<br />
              Sorra2025年度票選活動
            </p>
            <p className="text-center font-jinxuanlatte text-[30px] font-bold text-sorra-pink">
              立即點擊抽獎!
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center">
          {/* <p className="mt-8 text-sm">感謝您參與SORRA年度票選！</p> */}
          {/* <p className="mt-5 text-4xl font-bold">立即點擊抽獎</p> */}
          <div className="relative mt-7 aspect-square w-full overflow-hidden">
            <div ref={lottieRef} className="h-full w-full bg-transparent"></div>
            <div
              ref={tapRef}
              className="absolute left-[24%] top-[50%] aspect-square w-1/5"
            >
              <img
                src="/images/2024/tap.png"
                className="h-full w-full object-contain"
              />
              <div
                className="absolute left-[92%] top-[74%] h-2/3 w-2/3"
                onClick={async () => {
                  if (!user || stopRef.current) {
                    return;
                  }
                  // if (!myVotingAnswer) {
                  //   window.location.href = '/beautyawards2025/questions/1';
                  //   return;
                  // }
                  gsap
                    .to(tapRef.current, {
                      scale: 1.2,
                      duration: 0.1,
                      transformOrigin: 'right bottom',
                    })
                    .then(() => {
                      gsap.to(tapRef.current, {
                        scale: 0,
                        duration: 0.2,
                        transformOrigin: 'right bottom',
                      });
                    });
                  lottie.play('egg');

                  audioRef.current.play();

                  stopRef.current = true;
                  try {
                    const headers = await getAuthorizationHeader();

                    try {
                      await api.request({
                        method: 'GET',
                        url: `/api/beauty-award/me/votingAnswer`,
                        headers,
                      });
                    } catch (e) {
                      alert('請先完成投票問卷，才能參加抽獎！');
                      window.location.href = '/beautyawards2025/questions/1';
                      return;
                    }

                    const { data: targetAward } = await api.request({
                      method: 'POST',
                      url: '/api/beauty-award/luckyDraw',
                      headers,
                    });
                    const { id, lucky_draw } = targetAward;
                    const targetIndex = awards.findIndex(
                      (award) => award.id === id
                    );
                    setSelectedAward(awards[targetIndex]);
                    setLuckyDraw({ id: lucky_draw });
                  } catch (e) {
                    if (
                      e.response &&
                      e.response.data &&
                      e.response.data.message
                    ) {
                      alert(e.response.data.message);
                    }
                    // window.location.href = '/profile/redeem';
                  }
                }}
              ></div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 w-full px-[18px] pb-3 pt-8">
            <p className="w-full text-left text-[10px]">
              推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
            </p>
          </div>
        </div>
        {selectedAward && showAwardModal && (
          <Modal
            isOpen={showAwardModal}
            onRequestClose={() => {
              setShowAwardModal(false);
            }}
            shouldCloseOnOverlayClick={false}
            className="flex h-full w-full animate-popup-scale-in items-center justify-center bg-transparent px-6"
            overlayClassName="z-50 absolute top-0 left-0 right-0 bottom-0 w-full h-full p-safe-offset bg-[#111928] bg-opacity-50 backdrop-blur-sm animate-popup-overlay-fade-in"
          >
            <div className="flex w-full flex-col items-center rounded-2xl bg-white px-2.5 pb-8 pt-7 text-black">
              <p className="absolute bottom-[14px] left-4 text-[10px] text-white">
                推廣生意的競賽牌照號碼：60956-7 (只適用於抽獎)
              </p>
              <p className="text-[24px] font-bold text-sorra-pink">恭喜你!</p>
              <p className="mt-4 text-center text-base">
                感謝您參與Sa Sa美麗「程」獻 ：<br />
                Sorra2025年度票選活動
                <br />
                恭喜你獲得：
              </p>
              <div className="mt-5 flex w-full items-center justify-center gap-x-9">
                {!selectedAward.score_only && (
                  <div className="relative aspect-square h-[102px] w-[102px]">
                    <WebpImg
                      src={
                        selectedAward.thumbnail
                          ? selectedAward.thumbnail.url
                          : selectedAward.image
                            ? selectedAward.image.url
                            : '/images/placeholders/product.png'
                      }
                      className="h-full w-full object-contain"
                    />
                    {selectedAward.id == 1 && (
                      <div className="absolute bottom-0 right-0 translate-y-1/4 text-[32px] font-bold">
                        🇯🇵
                      </div>
                    )}
                  </div>
                )}
                {(selectedAward.score || 0) > 0 && (
                  <div className="aspect-square w-[95px]">
                    <img
                      src="/images/2024/coins.png"
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}
              </div>
              <div className="mt-5 flex flex-col items-center justify-center gap-y-2 font-rubik">
                <p className="whitespace-pre-line px-6 text-center text-[20px]">
                  {selectedAward.name}
                  {/* {selectedAward.score_only !== true &&
                    (selectedAward.score || 0) > 0 &&
                    ` + Sorra積分${selectedAward.score}分`} */}
                </p>
                {/* {!_.isNil(selectedAward.actual_price) && (
                  <p className="text-[14px]">
                    （價值HK${selectedAward.actual_price}）
                  </p>
                )} */}
              </div>
              <div className="mt-6">
                <button
                  className="min-w-[200px] rounded-full bg-sorra-pink px-5 py-3 text-white"
                  onClick={() => {
                    // setShowAwardModal(false);
                    // setSelectedAward(null);
                    // restart
                    stopRef.current = false;
                    setShowAwardFormModal(true);
                  }}
                >
                  確認得獎資料
                </button>
              </div>
              <Confetti
                width={width}
                height={height}
                recycle={false}
                gravity={0.09}
                friction={0.99}
                wind={0}
                numberOfPieces={260}
                initialVelocityY={{ min: 7, max: 10 }}
              />
            </div>
          </Modal>
        )}
        {showAwardFormModal && (
          <BeautyAward2025Modal
            show={showAwardFormModal}
            setShow={setShowAwardFormModal}
            award={selectedAward}
            luckyDraw={luckyDraw}
          />
        )}
      </div>
      <LandingModal show={showLandingModal} setShow={setShowLandingModal} />
    </>
  );
};

export default BeautyAwards2025LuckyReels;

export async function getServerSideProps(ctx) {
  const { key } = ctx.query;
  try {
    const { data: campaigns } = await api.request({
      method: 'GET',
      url: `/api/v3/campaigns`,
    });
    const targetCampaign = _.find(campaigns, { name: 'BEAUTY_AWARD_2025' });
    if (targetCampaign || key === 'F5A3302A-E6AB-44E7-ABEB-D41DECD1DCAA') {
      return {
        props: {},
      };
    } else {
      return {
        redirect: {
          destination: '/beautyawards2025',
          permanent: false,
        },
      };
    }
  } catch (e) {
    console.log(e);
  }
  return {
    notFound: true,
  };
}
